import { useCallback, useState } from 'react';
import { toPercentage, toCurrencyTruncated } from '@/utilities/formatting';
import { shallowEqual, useSelector } from 'react-redux';
import type { LocationState } from 'redux-first-router';
import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import { format } from 'date-fns';
import { getUTCDate } from '@utilities/date';
import { pathConstructor } from '@/hooks/useCompany/utils';
import {
  NarrativeKeyMetric,
  NarrativeKeyMetricChart,
} from './components/NarrativeKeyMetric';
import { SharePrice } from './components/SharePrice';
import type { HoverParam } from '@components/MiniPriceChart';
import PositiveNegativeValue from '@/components/PositiveNegativeValue';

const formatter = (value: number | null) =>
  value !== null ? toPercentage(value, 1) : 'n/a';
const formatTimestamp = (timestamp: number): string => {
  if (typeof timestamp !== 'number' || timestamp === 0) return 'N/A';
  return format(getUTCDate(timestamp), 'dd MMM, yyyy');
};

type Selected =
  | { status: 'loading' }
  | {
      marketCapListing: number;
      marketCapListingFormatted: string;
      tradingItemCurrencyISO: string;
      status: 'success';
      return7D: number | null;
      return7DFormatted: string;
      return1Y: number | null;
      return1YFormatted: string;
      companyId: string;
      analystCount: number;
      lastAnalysisUpdateFormatted: string;
      sharePriceFormatted: string;
    };
function selector(state: {
  company: CompanyState;
  location: LocationState;
}): Selected {
  try {
    const path = pathConstructor(state.location);
    const companyId = state.company.urlToIdMapping[path];
    const summary = state.company.summary[companyId];
    const analysis = state.company.analysis[companyId];
    const formatting = state.company.formatting[companyId]; ////
    const tradingItemCurrencyISO = formatting.tradingItemCurrencyISO;
    if (companyId === '') {
      return {
        status: 'loading',
      };
    }

    return {
      marketCapListing: analysis.marketCapRawData.listing,
      marketCapListingFormatted: toCurrencyTruncated(
        analysis.marketCapRawData.listing,
        1,
        tradingItemCurrencyISO
      ),
      tradingItemCurrencyISO,
      status: 'success',
      return7D: analysis.return7D,
      return7DFormatted: formatter(analysis.return7D),
      return1Y: analysis.return1YrAbs,
      return1YFormatted: formatter(analysis.return1YrAbs),
      companyId,
      analystCount: summary.analystCount,
      lastAnalysisUpdateFormatted: formatTimestamp(summary.lastAnalysisUpdate),
      sharePriceFormatted: toCurrencyTruncated(
        analysis.sharePrice,
        2,
        tradingItemCurrencyISO
      ),
    };
  } catch {
    return { status: 'loading' };
  }
}

export const PriceAnalyticsCard = () => {
  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);

  const [selectedTime, setSelectedTime] = useState<number | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);

  const onHover = useCallback(({ time, value }: HoverParam) => {
    setSelectedTime(time);
    setSelectedPrice(value);
  }, []);

  if (selected.status === 'loading') return null;

  const companyId = selected.companyId;

  return (
    <>
      <div className="grid grid-cols-[1fr_2fr] gap-2">
        <SharePrice
          companyId={companyId}
          time={selectedTime}
          sharePrice={selectedPrice}
          currencyISO={selected.tradingItemCurrencyISO}
        />
        <NarrativeKeyMetric companyId={companyId} sharePrice={selectedPrice} />
      </div>
      <NarrativeKeyMetricChart companyId={companyId} onHover={onHover} />
      <div className="grid grid-cols-[20%_1fr] gap-2 text-xs text-soft">
        <div>
          <span className="mr-1">7D</span>
          <span>
            <PositiveNegativeValue value={selected.return7D}>
              {selected.return7DFormatted}
            </PositiveNegativeValue>
          </span>
        </div>
        <div>
          <span className="mr-1">1Y</span>
          <span>
            <PositiveNegativeValue value={selected.return1Y}>
              {selected.return1YFormatted}
            </PositiveNegativeValue>
          </span>
        </div>
      </div>
    </>
  );
};
